import { useDebounce, useEventCallback } from "@stenajs-webui/core";
import { TextInput, type TextInputProps } from "@stenajs-webui/forms";
import {
  type FC,
  type FocusEventHandler,
  useCallback,
  useEffect,
  useState,
} from "react";

export interface DebouncedTextInputProps extends Omit<
  TextInputProps,
  "onChange" | "width" | "value" | "onValueChange" | "style"
> {
  delay?: number;
  editedValue: string;
  initialValue: string;
  maskInput?: (value: string) => string;
  onChange: (value: string) => void;
}

const defaultMaskInput = (value: string) => value;

export const DebouncedTextInput: FC<DebouncedTextInputProps> = ({
  editedValue,
  delay = 1000,
  initialValue,
  maskInput = defaultMaskInput,
  onChange,
  onBlur,
  ...props
}) => {
  const [value, setValue] = useState(editedValue);

  const onInputChange = useCallback(
    (inputValue: string) => {
      setValue(maskInput(inputValue));
    },
    [maskInput, setValue],
  );

  const onInternalBlur: FocusEventHandler<HTMLInputElement> = (event) => {
    onChange(value);
    onBlur?.(event);
  };

  useEffect(() => {
    setValue(editedValue);
  }, [editedValue]);

  const debouncedValue = useDebounce(value, delay);

  const handleDebouncedChange = useEventCallback(() => {
    if (initialValue !== value) {
      onChange(debouncedValue);
    }
  });

  useEffect(() => {
    handleDebouncedChange();
  }, [debouncedValue, handleDebouncedChange]);

  return (
    <TextInput
      {...props}
      onBlur={onInternalBlur}
      onValueChange={onInputChange}
      value={value}
      width={"100%"}
      style={{
        fontStyle: value !== initialValue ? "italic" : "normal",
        fontWeight: value !== initialValue ? "bold" : "normal",
      }}
    />
  );
};
